"use client";

import { useQuery } from "@tanstack/react-query";
import { useAccessToken, useTenantData } from "@/hooks/shared";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { TenantUserTasksApi } from "@/lib/user-tasks/tenant-user-tasks-api";
import type { UserTaskStatus } from "@/openapi/generated";

const DEFAULT_PAGE_SIZE = 100;

export function useTenantUserTasksGet(options?: {
  enabled?: boolean;
  page?: number;
  pageSize?: number;
  assigneeUserId?: number | null;
  status?: UserTaskStatus | null;
  overdueOnly?: boolean;
}) {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;
  const enabled = options?.enabled ?? true;
  const assigneeUserId = options?.assigneeUserId ?? undefined;
  const status = options?.status ?? undefined;
  const overdueOnly = options?.overdueOnly ?? false;

  return useQuery({
    queryKey: [
      QueryKeys.UserTasks,
      "tenant",
      tenantId,
      assigneeUserId ?? "all",
      status ?? "all",
      overdueOnly,
      page,
      pageSize,
    ],
    enabled:
      enabled && !!accessToken && tenantId != null && tenantId > 0,
    queryFn: async () => {
      const token = accessToken;
      const tid = tenantId;
      if (!token || tid == null) {
        throw new Error("Missing session or tenant for tenant tasks");
      }
      const service = createAuthenticatedApi(TenantUserTasksApi, token);
      return service.apiUserTasksTenantGet({
        xTenantID: tid,
        page,
        pageSize,
        assigneeUserId:
          assigneeUserId != null && assigneeUserId > 0
            ? assigneeUserId
            : undefined,
        status: status ?? undefined,
        overdue: overdueOnly ? true : undefined,
      });
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}
